import { Component, inject, Input, OnInit, signal } from '@angular/core';
import { TakeUntilDestroy } from '@app/shared/decorators/take-until-destroy.decorator';
import { Ranking } from '@app/shared/models/ranking.model';
import { LoggerService } from '@app/shared/services/logger.service';
import { RankingService } from '@app/shared/services/ranking.service';
import { RealtimeService } from '@app/shared/services/realtime.service';
import { lastValueFrom, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ParagraphComponent } from '@shared/components/text/p/p.component';
import { AnimatedTimerComponent } from '@app/shared/components/animated-timer/animated-timer.component';
import { ButtonComponent } from '@app/shared/components/button/button.component';
import { UpperCasePipe, DecimalPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@TakeUntilDestroy
@Component({
  selector: 'slotcom-ranking-prize',
  templateUrl: './ranking-prize.component.html',
  styleUrls: ['./ranking-prize.component.scss'],
  standalone: true,
  imports: [ParagraphComponent, AnimatedTimerComponent, ButtonComponent, UpperCasePipe, DecimalPipe, TranslateModule],
})
export class RankingPrizeComponent implements OnInit {
  @Input() isAwardPending: boolean;

  award = signal<Ranking | null>(null);
  isRedeeming = signal(false);
  date: Date = this.getEndDate();
  time: string;
  finished: boolean = false;

  componentDestroy: () => Observable<unknown>;

  private readonly rankingService = inject(RankingService);
  public readonly logger = inject(LoggerService);
  private readonly realtimeService = inject(RealtimeService);

  async ngOnInit() {
    this.award.set(await lastValueFrom(this.getAward()));

    this.realtimeService
      .on('RankingFinished')
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe({
        next: async () => this.award.set(await lastValueFrom(this.getAward())),
        error: (error) => this.logger.error(error),
      });
  }

  getAward(): Observable<Ranking> {
    return this.rankingService.getAward();
  }

  redeemAward = async (): Promise<void> => {
    this.isRedeeming.set(true);
    try {
      this.award.set(await lastValueFrom(this.rankingService.redeemAward()));
      this.rankingService.notifyRedeemAward();
    } catch (err) {
      this.logger.error(err);
    } finally {
      this.isRedeeming.set(false);
    }
  };

  getEndDate(): Date {
    const date = new Date();
    date.setUTCHours(0, 0, 0, 0);
    date.setUTCDate(16);
    if (date < new Date()) {
      const month = date.getUTCMonth();
      date.setUTCMonth(month + 1);
    }
    return date;
  }
}
